<script lang="ts">
  import {createEventDispatcher, setContext} from 'svelte';
  import {get} from 'svelte/store';
  import {create as validation} from 'Utility/validations/validations';
  import errors from 'Store/FormValidationStore';
  import type {
    AnyObject,
    ValidationSchema,
    CommonInput,
    ValidationError,
  } from 'Utility/validations/types';
  import type {CustomElementEvent, CustomSubmitEvent} from 'Types/event';
  import {getElementById} from 'Utility/document';

  export let validationSchema: ValidationSchema = {};
  export let validateOnInput: boolean = true;
  export let validateOnBlur: boolean = false;
  export let debounceValidation: boolean = false;
  export let returnEmptyData: boolean = true;
  export let groupData: boolean = false;
  export let id: string = '';
  // eslint-disable-next-line no-unused-vars
  export let afterValidation: (errors: {
    [inputName: string]: ValidationError;
  }) => void = null;

  let formEl: HTMLFormElement;
  const validationTimeoutIds: {
    [key: string]: ReturnType<typeof setTimeout>;
  } = {};
  const debounceMs: number = 300;

  const dispatch = createEventDispatcher();

  const validateEvent = (e: CustomElementEvent<HTMLInputElement>): void => {
    const validators: string = validationSchema[e.target.name];

    validation(validators, e.target.name, e.target.value).validate();
    afterValidation && afterValidation(get(errors));
  };

  const hasValidators = (e: CustomElementEvent<HTMLInputElement>): boolean => {
    return Boolean(validationSchema[e.target.name]);
  };

  const onInput = (e: CustomElementEvent<HTMLInputElement>): void => {
    if (!validateOnInput || !hasValidators(e)) return;

    if (debounceValidation) {
      clearTimeout(validationTimeoutIds[e.target.name]);
      validationTimeoutIds[e.target.name] = setTimeout(
        () => validateEvent(e),
        debounceMs
      );
    } else {
      validateEvent(e);
    }
  };

  const onBlur = (e: CustomElementEvent<HTMLInputElement>): void => {
    if (!validateOnBlur || !hasValidators(e)) return;

    validateEvent(e);
  };

  const onChange = (name: string, value: CommonInput): void => {
    const validators: string = validationSchema[name];

    if (validators) validation(validators, name, value).validate();
  };

  const validateField = (data: AnyObject): void => {
    Object.entries(data).forEach(([key, value]) => {
      const validators: string = validationSchema[key];

      if (validators) validation(validators, key, value).validate();
    });
  };

  const isValid = (): boolean => {
    return !Object.values($errors).some(field => field.error);
  };

  export function reset(): void {
    formEl.reset();
  }

  export function resetErrors(): void {
    errors.resetErrors();
  }

  export const scrollToTop = (): void => {
    getElementById(id).scrollIntoView({behavior: 'smooth'});
  };

  const groupKeys = (
    object: Object,
    key: string,
    value: string | object | unknown
  ): Object => {
    const keys: Array<string> = key.split('.');
    const last: string = keys.pop();

    keys.reduce((o, k) => (o[k] ??= {}), object)[last] = value;
    return object;
  };

  const groupFormData = (data: Object): Object => {
    return Object.entries(data).reduce((r, [k, v]) => groupKeys(r, k, v), {});
  };

  const removeEmptyValues = (dataObj: Object): Object => {
    const data: Object = {};

    Object.entries(dataObj).forEach(([key, value]) => {
      if (value && value instanceof File && value.size > 0) data[key] = value;
      else if (value && !(value instanceof File)) data[key] = value;
    });

    return data;
  };

  const formatData = (data: Object): Object => {
    if (!returnEmptyData && groupData) {
      return groupFormData(removeEmptyValues(data));
    }

    if (!returnEmptyData) {
      return removeEmptyValues(data);
    }

    if (groupData) {
      return groupFormData(data);
    }

    return data;
  };

  const onSubmit = (event: CustomSubmitEvent<HTMLFormElement>): void => {
    const formData: FormData = new FormData(event.target);
    const data: AnyObject = {};

    for (const field of formData.entries()) {
      const [key, value] = field;
      data[key] = value;
    }

    if (Object.keys(validationSchema).length) validateField(data);

    dispatch('submit', {isValid: isValid(), data: formatData(data)});
  };

  setContext('form', {errors, onInput, onChange, onBlur});
</script>

<form
  on:submit|preventDefault={onSubmit}
  bind:this={formEl}
  {id}
  class={$$props.class}
  data-testid="form-{id}"
  on:focusin
  on:focusout
  on:click
  on:input
>
  <slot />
</form>

<style lang="scss">
  .full-height {
    height: 100%;
  }
</style>
